import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ModalLayout from '../layouts/ModalLayout'
import MessageBox from './MessageBox'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const emptyForm = {
  company_name: '',
  contact_person: '',
  email: '',
  phone: '',
  note: ''
}

const fieldStyle = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1.5px solid #1e3f44',
  borderRadius: 8,
  padding: '9px 10px',
  background: '#fff',
  color: '#000',
  fontSize: 15,
  outline: 'none'
}

const cardStyle = {
  background: '#fff',
  border: '1px solid #1e3f44',
  borderRadius: 12,
  padding: 12,
  marginBottom: 8
}

export default function AdvertisementOrderModal({
  open,
  advertisementType,
  onClose
}) {
  const { t, i18n } = useTranslation('advertisement')
  const [bannerType, setBannerType] = useState('static')
  const [form, setForm] = useState(emptyForm)
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState(null)
  const [successOpen, setSuccessOpen] = useState(false)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [pressedBtn, setPressedBtn] = useState(null)

useEffect(() => {
  if (open) return

  setBannerType('static')
  setForm(emptyForm)
  setSubmitting(false)
  setMessage(null)
  setSuccessOpen(false)
  setHoverBtn(null)
  setPressedBtn(null)
}, [open])

  const multiplier = useMemo(() => {
    if (!advertisementType) return 1
    if (bannerType === 'dynamic') return Number(advertisementType.dynamic_multiplier || 1)
    if (bannerType === 'video') return Number(advertisementType.video_multiplier || 1)
    return 1
  }, [advertisementType, bannerType])

  const totalPrice = useMemo(() => {
    return Number((Number(advertisementType?.price || 0) * multiplier).toFixed(2))
  }, [advertisementType, multiplier])

  const validateForm = () => {
    const companyName = form.company_name.trim()
    const contactPerson = form.contact_person.trim()
    const email = form.email.trim()
    const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

    if (!companyName) return t('companyRequired')
    if (companyName.length < 2) return t('companyTooShort')
    if (!contactPerson) return t('contactRequired')
    if (contactPerson.length < 2) return t('contactTooShort')
    if (!email) return t('emailRequired')
    if (!emailRegex.test(email)) return t('emailInvalid')

    return ''
  }

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
    setMessage(null)
    setSuccessOpen(false)
  }

  const submitOrder = async () => {
    if (!advertisementType) return

    setSuccessOpen(false)
    setMessage(null)

    const validationMessage = validateForm()
    if (validationMessage) {
      setMessage({ type: 'error', message: validationMessage })
      return
    }

    setSubmitting(true)

    try {
      const res = await fetchWithAuth(
        '/advertisement/orders',
        {
          method: 'POST',
          body: JSON.stringify({
            advertisement_type_id: advertisementType.id,
            banner_type: bannerType,
            company_name: form.company_name.trim(),
            contact_person: form.contact_person.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            note: form.note.trim()
          })
        },
        i18n.language || 'sk'
      )

      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        setMessage({ type: 'error', message: data.message || t('submitError') })
        return
      }

      setMessage(null)
      setSuccessOpen(true)
    } catch (err) {
      console.error(err)
      setMessage({ type: 'error', message: t('submitError') })
    } finally {
      setSubmitting(false)
    }
  }

const handleClose = () => {
  setHoverBtn(null)
  setPressedBtn(null)
  onClose()
}

  if (!open || !advertisementType) return null

  const closeSuccess = () => {
    setSuccessOpen(false)
    onClose()
  }

  const radioRow = (value, label) => (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        padding: '3px 0'
      }}
    >
      <input
        type="radio"
        name="bannerType"
        checked={bannerType === value}
        onChange={() => setBannerType(value)}
      />
      <span>{label}</span>
    </label>
  )

  return (
    <>
      <ModalLayout open={open && !successOpen} onClose={handleClose} size="medium" zIndex={1000000}>
        <h3 style={{ fontSize: 24, margin: 0, marginBottom: 8, textAlign: 'center' }}>
          {t('orderTitle')}
        </h3>

        <div style={{ overflowY: 'auto', flex: 1, paddingRight: 4 }}>
          <div style={cardStyle}>
            <div style={{ fontWeight: 700, fontSize: 18 }}>
              {t('selectedPackage')}: {advertisementType.name}
            </div>
            <div style={{ marginTop: 2 }}>
              {t('impressionsLabel')}: {Number(advertisementType.impressions).toLocaleString()}
            </div>
            <div style={{ marginTop: 2 }}>
              {t('priceLabel')}: {Number(advertisementType.price).toLocaleString()} €
            </div>
          </div>

          <div style={cardStyle}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('typeTitle')}</div>
            {radioRow('static', t('types.static'))}
            {radioRow('dynamic', t('types.dynamic'))}
            {radioRow('video', t('types.video'))}
          </div>

          <div style={cardStyle}>{t('nextSteps')}</div>

          <div style={{ ...cardStyle, background: '#3BC6C4' }}>
            <strong>
              {t('totalPrice')}: {totalPrice.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })} €
            </strong>
          </div>

          <div style={{ display: 'grid', gap: 8, padding: '0 2px 8px' }}>
            <input
              style={fieldStyle}
              value={form.company_name}
              placeholder={t('companyName')}
              onChange={e => updateField('company_name', e.target.value)}
            />
            <input
              style={fieldStyle}
              value={form.contact_person}
              placeholder={t('contactPerson')}
              onChange={e => updateField('contact_person', e.target.value)}
            />
            <input
              style={fieldStyle}
              type="email"
              value={form.email}
              placeholder="Email"
              onChange={e => updateField('email', e.target.value)}
            />
            <input
              style={fieldStyle}
              type="tel"
              value={form.phone}
              placeholder={t('phone')}
              onChange={e => updateField('phone', e.target.value)}
            />
            <textarea
              style={{ ...fieldStyle, minHeight: 82, resize: 'vertical' }}
              value={form.note}
              placeholder={t('note')}
              onChange={e => updateField('note', e.target.value)}
            />
          </div>
        </div>

        {message && <MessageBox message={message.message} type={message.type} />}

        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 8 }}>
          <button
            style={modalPrimaryButtonStyle(pressedBtn, 'submit', hoverBtn === 'submit')}
            onMouseEnter={() => setHoverBtn('submit')}
            onMouseLeave={() => setHoverBtn(null)}
            onMouseDown={() => setPressedBtn('submit')}
            onMouseUp={() => setPressedBtn(null)}
            onClick={submitOrder}
            disabled={submitting}
          >
            {submitting ? t('submitting') : t('submitButton')}
          </button>
	<button
	  style={modalSecondaryButtonStyle(pressedBtn, 'close', hoverBtn === 'close')}
	  onMouseEnter={() => setHoverBtn('close')}
	  onMouseLeave={() => {
  	  setHoverBtn(null)
  	  setPressedBtn(null)
	  }}
	  onMouseDown={() => setPressedBtn('close')}
 	 onMouseUp={() => setPressedBtn(null)}
 	 onClick={handleClose}
 	 disabled={submitting}
	>
	  {t('closeButton')}
	</button>
        </div>
      </ModalLayout>

      <ModalLayout open={successOpen} onClose={closeSuccess} size="small" zIndex={1000001}>
        <h3 style={{ marginTop: 0 }}>{t('orderTitle')}</h3>
        <p>{t('submitSuccess')}</p>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            style={modalPrimaryButtonStyle(pressedBtn, 'ok', hoverBtn === 'ok')}
            onMouseEnter={() => setHoverBtn('ok')}
            onMouseLeave={() => setHoverBtn(null)}
            onMouseDown={() => setPressedBtn('ok')}
            onMouseUp={() => setPressedBtn(null)}
            onClick={closeSuccess}
          >
            OK
          </button>
        </div>
      </ModalLayout>
    </>
  )
}